import { useState } from "react";

const FRONT_PARTS = [
  { id: "Head", type: "circle", cx: 100, cy: 30, r: 22 },
  { id: "Neck", type: "rect", x: 91, y: 52, w: 18, h: 14, rx: 4 },
  { id: "Chest", type: "rect", x: 70, y: 68, w: 60, h: 52, rx: 8 },
  { id: "Abdomen", type: "rect", x: 74, y: 122, w: 52, h: 42, rx: 8 },
  { id: "Pelvis", type: "rect", x: 76, y: 166, w: 48, h: 34, rx: 8 },
  { id: "Left shoulder", type: "circle", cx: 58, cy: 76, r: 10 },
  { id: "Right shoulder", type: "circle", cx: 142, cy: 76, r: 10 },
  { id: "Left upper arm", type: "rect", x: 42, y: 88, w: 15, h: 52, rx: 6 },
  { id: "Right upper arm", type: "rect", x: 143, y: 88, w: 15, h: 52, rx: 6 },
  { id: "Left forearm", type: "rect", x: 40, y: 142, w: 14, h: 48, rx: 6 },
  { id: "Right forearm", type: "rect", x: 146, y: 142, w: 14, h: 48, rx: 6 },
  { id: "Left hand", type: "circle", cx: 47, cy: 200, r: 10 },
  { id: "Right hand", type: "circle", cx: 153, cy: 200, r: 10 },
  { id: "Left thigh", type: "rect", x: 78, y: 202, w: 20, h: 66, rx: 6 },
  { id: "Right thigh", type: "rect", x: 102, y: 202, w: 20, h: 66, rx: 6 },
  { id: "Left knee", type: "circle", cx: 88, cy: 278, r: 10 },
  { id: "Right knee", type: "circle", cx: 112, cy: 278, r: 10 },
  { id: "Left shin", type: "rect", x: 79, y: 290, w: 18, h: 62, rx: 6 },
  { id: "Right shin", type: "rect", x: 103, y: 290, w: 18, h: 62, rx: 6 },
  { id: "Left foot", type: "ellipse", cx: 88, cy: 364, rx: 12, ry: 8 },
  { id: "Right foot", type: "ellipse", cx: 112, cy: 364, rx: 12, ry: 8 },
];

const BACK_PARTS = [
  { id: "Head (back)", type: "circle", cx: 100, cy: 30, r: 22 },
  { id: "Upper back", type: "rect", x: 70, y: 68, w: 60, h: 52, rx: 8 },
  { id: "Lower back", type: "rect", x: 74, y: 122, w: 52, h: 42, rx: 8 },
  { id: "Buttocks", type: "rect", x: 76, y: 166, w: 48, h: 34, rx: 8 },
  { id: "Left upper arm (back)", type: "rect", x: 42, y: 88, w: 15, h: 52, rx: 6 },
  { id: "Right upper arm (back)", type: "rect", x: 143, y: 88, w: 15, h: 52, rx: 6 },
  { id: "Left forearm (back)", type: "rect", x: 40, y: 142, w: 14, h: 48, rx: 6 },
  { id: "Right forearm (back)", type: "rect", x: 146, y: 142, w: 14, h: 48, rx: 6 },
  { id: "Left hand (back)", type: "circle", cx: 47, cy: 200, r: 10 },
  { id: "Right hand (back)", type: "circle", cx: 153, cy: 200, r: 10 },
  { id: "Left hamstring", type: "rect", x: 78, y: 202, w: 20, h: 66, rx: 6 },
  { id: "Right hamstring", type: "rect", x: 102, y: 202, w: 20, h: 66, rx: 6 },
  { id: "Left calf", type: "rect", x: 79, y: 290, w: 18, h: 62, rx: 6 },
  { id: "Right calf", type: "rect", x: 103, y: 290, w: 18, h: 62, rx: 6 },
  { id: "Left heel", type: "ellipse", cx: 88, cy: 364, rx: 12, ry: 8 },
  { id: "Right heel", type: "ellipse", cx: 112, cy: 364, rx: 12, ry: 8 },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function Shape({ p, selected, onToggle }) {
  const common = {
    onClick: () => onToggle(p.id),
    "data-testid": `body-part-${slug(p.id)}`,
    fill: selected ? "#D5281B" : "#E2E8F0",
    stroke: selected ? "#991B1B" : "#94A3B8",
    strokeWidth: 2,
    className: "cursor-pointer transition-colors duration-150 hover:fill-[#F8B4B4]",
  };
  const title = <title>{p.id}</title>;
  if (p.type === "circle") return <circle cx={p.cx} cy={p.cy} r={p.r} {...common}>{title}</circle>;
  if (p.type === "ellipse") return <ellipse cx={p.cx} cy={p.cy} rx={p.rx} ry={p.ry} {...common}>{title}</ellipse>;
  return <rect x={p.x} y={p.y} width={p.w} height={p.h} rx={p.rx} {...common}>{title}</rect>;
}

export default function BodyMap({ selected = [], onToggle }) {
  const [view, setView] = useState("front");
  const parts = view === "front" ? FRONT_PARTS : BACK_PARTS;
  return (
    <div className="shrink-0" data-testid="body-map">
      <div className="flex gap-2 mb-3 justify-center">
        <button type="button" onClick={() => setView("front")} data-testid="bodymap-front"
          className={`h-10 px-4 rounded-lg text-xs font-bold border transition-colors ${view === "front" ? "bg-[#0F2547] text-white border-[#0F2547]" : "border-slate-300 text-slate-600"}`}>
          Front
        </button>
        <button type="button" onClick={() => setView("back")} data-testid="bodymap-back"
          className={`h-10 px-4 rounded-lg text-xs font-bold border transition-colors ${view === "back" ? "bg-[#0F2547] text-white border-[#0F2547]" : "border-slate-300 text-slate-600"}`}>
          Back
        </button>
      </div>
      <svg viewBox="0 0 200 380" className="w-48 sm:w-56 mx-auto select-none">
        {parts.map((p) => (
          <Shape key={p.id} p={p} selected={selected.includes(p.id)} onToggle={onToggle} />
        ))}
      </svg>
    </div>
  );
}
